import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

export interface ServiceItem {
  id: string;
  name: string;
  price: number;
  taxRate?: number;
  variants?: any[];
}

export interface PaginatedServicesResponse {
  items: ServiceItem[];
  meta: {
    totalItems: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages: number;
    currentPage: number;
  };
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

interface InvoiceItemPayload {
  serviceId: string;
  total: number;
}

interface ServiceInvoicePayload {
  recipientId: string;
  invoiceDate: string;
  gstAmount: number;
  discount: number;
  total: number;
  items: InvoiceItemPayload[];
  payments: any[];
}
// --- END DEFINITIONS / INTERFACES ---

@Injectable({
  providedIn: 'root',
})
export class ServiceBillingService {
  private baseUrl = environment.apiUrl;
  http = inject(HttpClient);

  private getAuthHeaders(): HttpHeaders {
    const token =
      localStorage.getItem('access_token') || 'YOUR_BEARER_TOKEN_HERE';

    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  // --- Service Invoice Endpoints ---

  createServiceInvoice(payload: ServiceInvoicePayload): Observable<any> {
    const headers = this.getAuthHeaders();
    console.log(`Payload: ${JSON.stringify(payload)}`);
    console.log(`service-invoices: ${this.baseUrl}`);
    return this.http.post(`${this.baseUrl}/service-invoices`, payload, {
      headers,
    });
  }

  getServiceInvoices(
    page: number,
    limit: number,
    search?: string,
  ): Observable<any> {
    const headers = this.getAuthHeaders();
    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('limit', limit.toString());
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get(`${this.baseUrl}/service-invoices`, {
      headers,
      params,
    });
  }

  getServiceInvoiceById(id: string): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.get(`${this.baseUrl}/service-invoices/${id}`, {
      headers,
    });
  }

  // --- Services Endpoint ---

  getServices(
    page: number,
    limit: number,
    search: string = '',
  ): Observable<PaginatedServicesResponse> {
    const headers = this.getAuthHeaders();

    let params = new HttpParams();
    params = params.set('page', page.toString());
    params = params.set('limit', limit.toString());

    // <--- ADDED LOGIC: Include the search term if it's provided and not empty
    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<PaginatedServicesResponse>(
      `${this.baseUrl}/services`,
      {
        headers,
        params,
      },
    );
  }
  addRecipient(payload: {
    name: string;
    email: string;
    phone: string;
    address: string;
    clientId: string;
  }): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(`${this.baseUrl}/recipients`, payload, { headers });
  }

  getRecipients(
    paramsObj: { page?: number; limit?: number; search?: string } = {},
  ): Observable<any> {
    const headers = this.getAuthHeaders();
    let params = new HttpParams();
    if (paramsObj.page) params = params.set('page', paramsObj.page.toString());
    if (paramsObj.limit)
      params = params.set('limit', paramsObj.limit.toString());
    if (paramsObj.search) params = params.set('search', paramsObj.search);
    return this.http.get(`${this.baseUrl}/recipients`, { headers, params });
  }

  // Get recipient by ID
  getRecipientById(id: string): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.get(`${this.baseUrl}/recipients/${id}`, { headers });
  }

  generateInvoice(id: string, payload: any): Observable<any> {
    const headers = this.getAuthHeaders();

    // 1. Convert the payload object into HttpParams for a GET request
    let params = new HttpParams();
    if (payload) {
      // Assuming payload is simple key-value for query parameters
      Object.keys(payload).forEach((key) => {
        // Note: Ensure all payload values are strings or numbers for HttpParams
        params = params.set(key, payload[key]);
      });
    }

    // 2. Use HttpClient.get() and pass the options correctly
    //    - The second argument is the options object.
    //    - We add responseType: 'blob' since we are fetching a file (PDF).
    return this.http.get(`${this.baseUrl}/service-invoices/${id}/pdf`, {
      headers: headers,
      params: params, // <-- Pass payload as query parameters
      responseType: 'blob', // <-- Set response type for file handling (PDF)
    });
  }

  getProviders(
    paramsObj: { page?: number; limit?: number; search?: string } = {},
  ): Observable<any> {
    console.log(`paramsObj: ${JSON.stringify(paramsObj)}`);
    const headers = this.getAuthHeaders();
    let params = new HttpParams();
    if (paramsObj.page) params = params.set('page', paramsObj.page.toString());
    if (paramsObj.limit)
      params = params.set('limit', paramsObj.limit.toString());
    if (paramsObj.search)
      params = params.set('search', paramsObj.search.trim());

    return this.http.get(`${this.baseUrl}/service-providers`, {
      headers,
      params,
    });
  }

  /**
   * POST /service-providers
   * Add a new service provider
   */
  addProvider(payload: { name: string; email?: string }): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(`${this.baseUrl}/service-providers`, payload, {
      headers,
    });
  }
  /**
   * POST /service-invoices/export-xlsx
   * Export service invoices report
   */
  exportServiceInvoices(payload: any): Observable<Blob> {
    const headers = this.getAuthHeaders();
    return this.http.post(
      `${this.baseUrl}/service-invoices/export-xlsx`,
      payload,
      {
        headers,
        responseType: 'blob',
      },
    );
  }
}
